/* 
Format => MAJOR.MINOR.PATCH
Documentation: https://semver.org/

* VERSIONING :
- MAJOR => When you make incompatible API changes.
- MINOR => When you add functionality in a backward compatible manner.
- PATCH => When you make backward compatible bug fixes.

* CHANGE TYPES :
- chore       => Changes that do not affect the application's functionality or user experience.
- bugfix      => Fixes for issues or bugs in the application
- enhancement => Improvements to existing functionality without adding new features.
- feature     => Addition of new functionality or features to the application.

*/

interface StrictLog {
  hidden?: boolean;
  version:
  | `${number}.${number}.${number}`
  | `${number}.${number}.${number}-alpha.${number}`
  | `${number}.${number}.${number}-beta.${number}`
  | `${number}.${number}.${number}-rc.${number}`; // e.g., 0.1.0 
  release_date: `${number}-${number}-${number}T${number}:${number}:${number}Z`; // e.g., 2025-01-31T00:00:00Z
  changes: StrictChangeEntry[];
}
interface StrictChangeEntry {
  type: 'chore' | 'bugfix' | 'enhancement' | 'feature';
  title: string;
  description: string | string[];
  hidden?: boolean;
  issue_number?: number;
}

export const changelog: StrictLog[] = [
  {
    version: '0.1.0',
    release_date: '2025-01-31T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'PROD_ASSETS_URL Fix',
        description: 'Fixed PROD_ASSETS_URL configuration.',
      },
      {
        type: 'enhancement',
        title: 'API Development Improvements',
        description: 'Improved API development for DNS, SSL, and secret APIs pages.',
      },
      {
        type: 'enhancement',
        title: 'Responsive Improvements',
        description: 'Made responsive improvements across the application.',
      },
      {
        type: 'chore',
        title: 'Initial Version Set',
        description: 'Set initial version to 0.1.0.',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'AddDomain Form Defaults',
        description: 'Set default non-ready booleans to false in AddDomain form.',
        hidden: true,
      },
    ],
  },
  {
    version: '0.2.0',
    release_date: '2025-02-22T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Version Change Log',
        description: 'Added a popup to display version history, including features, enhancements, and bug fixes.',
      },
      {
        type: 'feature',
        title: 'Malicious Table and Side Menu Improvements',
        description: 'Added a malicious table, improved the side menu, restructured the request table, and added log clearing functionality.',
      },
      {
        type: 'feature',
        title: 'Targets Table Collapsible',
        description: 'Made the targets table collapsible for better usability.',
      },
      {
        type: 'bugfix',
        title: 'Scan Now Button Responsiveness',
        description: 'Fixed the "Scan Now" button to provide immediate feedback and proper loading icon alignment.',
      },
      {
        type: 'bugfix',
        title: 'Newly Added Domain Auto-Selection',
        description: 'Ensured newly added domains are automatically selected in the left panel.',
      },
      {
        type: 'bugfix',
        title: 'Fixed Inability to Delete Last Remaining Domain',
        description: 'Resolved an issue preventing users from deleting the last remaining domain.',
      },
      {
        type: 'bugfix',
        title: 'AzureAuthProvider Update',
        description: 'Updated authentication to handle interaction_in_progress error.',
      },
      {
        type: 'enhancement',
        title: 'Scanned Domains Display Improvement',
        description: 'Highlighted selected domains for better visibility in the sidebar and targets panel.',
      },
      {
        type: 'enhancement',
        title: 'External Links Behavior',
        description: 'Updated external links to open in a new tab for improved navigation.',
      },
      {
        type: 'enhancement',
        title: 'Tooltip for Edit and Delete Buttons',
        description: 'Added tooltips to the Edit and Delete buttons in the Targets panel for better usability.',
      },
      {
        type: 'enhancement',
        title: 'Sidebar and Page Transformations',
        description: 'Refined UI for the sidebar, new domain page, and target page.',
      },
      {
        type: 'enhancement',
        title: 'New Domain Popup Confirmation Fix',
        description: 'Fixed new domain popup confirmation.',
      },
      {
        type: 'enhancement',
        title: 'Improved fetchSingleApi Handling',
        description: 'Improved handling of fetchSingleApi.',
        hidden: true,
      },
      {
        type: 'enhancement',
        title: 'Interval Requests for scanStatus',
        description: 'Added interval requests for scanStatus in fetchSingleApi.',
      },
      {
        type: 'enhancement',
        title: 'Search Dialog Styling',
        description: 'Styled search dialog.',
        hidden: true,
      },
      {
        type: 'bugfix',
        title: 'Jenkins Fix',
        description: 'Fixed Jenkins issues and added comment-in auto-deploy.',
      },
      {
        type: 'bugfix',
        title: 'BASE_URL Update',
        description: 'Updated BASE_URL configuration.',
        hidden: true,
      },
      {
        type: 'bugfix',
        title: 'Xcopy Arguments Update',
        description: 'Updated xcopy arguments to resolve deployment errors.',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'Minor Fix',
        description: 'Applied a minor fix.',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'Minor Changes',
        description: 'Applied minor changes.',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'Web.config Copying Capability',
        description: 'Added web.config copying capability to the main branch and reactivated auto-deployment (pipeline stopped manually before deployment).',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'Code Cleanup and Type Error Fixes',
        description: 'Cleaned up code and fixed type errors.',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'Dist Copy Filter Adjustment',
        description: 'Adjusted dist copy filter for testing copy functionality.',
        hidden: true,
      },
      {
        type: 'chore',
        title: 'Path Name Adjustment for Xcopy',
        description: 'Adjusted path name for xcopy.',
        hidden: true,
      },
    ],
  },
  {
    version: '0.2.1',
    release_date: '2025-02-25T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Table searchbox',
        description: 'Added a search functionality to filter results.',
      },
      {
        type: 'enhancement',
        title: 'Responsive fix',
        description: 'Enhanced responsiveness for table action buttons.',
      },
      {
        type: 'bugfix',
        title: 'Page search fix',
        description: 'Parent items with no corresponding page are removed from filtered results.',
      },
    ],
  },
  {
    version: '0.2.2',
    release_date: '2025-02-26T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Vulnerabilities table collapsible',
        description: 'Made the vulnerabilities table collapsible for better usability.',
      },
      {
        type: 'enhancement',
        title: 'Responsive fix',
        description: 'Enhanced responsiveness for table action buttons, and version log.',
      },
      {
        type: 'chore',
        title: 'Theme color',
        description: 'Table header row color and height is updated.',
      },
    ],
  },
  {
    version: '0.2.3',
    release_date: '2025-02-28T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Minor fixes',
        description: ['Prevent access to the UI when the server cannot be reached,', 'Typo mistakes,'],
      },
      {
        type: 'enhancement',
        title: 'No incident image',
        description: ['Added a "No Incident" image component for all tables'],
      },
    ],
  },
  {
    version: '0.2.4',
    release_date: '2025-03-02T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Easier domain registry',
        description: [
          'Domain dialog responsive adjustments,',
          'Automatical removal of protocol prefixes,',
          'Improved state management with less re-renders,',
          'Improved domain validation helper to ensure valid domain formats'
        ],
      },
      {
        type: 'bugfix',
        title: 'Azure Auth Error Handling',
        description: 'Improved error handling for Azure authentication.',
      },
      {
        type: 'enhancement',
        title: 'Table View Updated',
        description: ['Chips for scan types in targets page,', 'Improved table feedback with transitions among spinner, no-data message, and no-incident image for a better user experience,'],
      },
    ],
  },
  {
    version: '0.2.5',
    release_date: '2025-03-08T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'New page',
        description: 'Sensitive resources page is added.',
      },
      {
        type: 'enhancement',
        title: 'Optimized initial loading time',
        description: ['Reduced initial loading time for better user experience.'],
      },
      {
        type: 'feature',
        title: 'New tab for subdomains table',
        description: "Added an 'IP Addresses' tab to the subdomains page.",
      },
      {
        type: 'chore',
        title: 'Updated table view',
        description: ['Fixed some prefixes and capitalized some abbreviations.'],
      },
    ],
  },
  {
    version: '0.2.6',
    release_date: '2025-03-09T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Image compression',
        description: ['Reduced image sizes for shorter loading time.'],
      },
      {
        type: 'feature',
        title: 'Pagination for subdomains page',
        description: 'Static pagination and page size for subdomains page.',
      },
      {
        type: 'chore',
        title: 'Updated table view',
        description: ['Merged two tabs ito one in sensitive files page,', 'Page is renamed as Sensitive Files & Paths,'],
      },
    ],
  },
  {
    version: '0.3.0',
    release_date: '2025-03-14T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Dynamic Domain-Add Popup',
        description: ['Options for the popup are now dynamically fetched from the API,', 'The Domain-Add Popup is designed to support editing existing scans.'],
      },
      {
        type: 'enhancement',
        title: 'Targets Table Update',
        description: ['Implemented a new API structure for the Targets table,', 'Improved the collapsible row content view for better usability.'],
      },
      {
        type: 'chore',
        title: 'No Incidents Image Update',
        description: ["The 'No Incidents' image has been updated."],
      },
    ],
  },
  {
    version: '0.3.1',
    release_date: '2025-03-15T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Pagination for ports page',
        description: 'Static pagination and page size for ports page.',
      },
      {
        type: 'bugfix',
        title: 'Domain add-edit popup',
        description: ['Fixed bugs related to the domain add-edit popup.'],
      },
      {
        type: 'chore',
        title: 'View Change',
        description: ['Target Settings now opens in a popup instead of a separate page.'],
      },
    ],
  },
  {
    version: '0.3.2',
    release_date: '2025-03-16T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Report APIs feedback',
        description: 'Page will display a toast message on an error.',
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Strict type checkings.'],
      },
    ],
  },
  {
    version: '0.3.3',
    release_date: '2025-03-18T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'New page',
        description: ['Subdomain takeover page is added,', 'Standalone mode via webmanifest.'],
      },
      {
        type: 'bugfix',
        title: 'Pre-selected scan type',
        description: ['Bug fixes related to pre-selected scan type,', 'Ports tab switch bugfix,', 'Ports table pagination fix,', 'Sidebar responsive fix,', 'Ports no-item info fix.'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Added meta tags,', 'Compiler settings for cache.'],
      },
    ],
  },
  {
    version: '0.3.4',
    release_date: '2025-03-20T00:00:00Z',
    changes: [
      {
        type: 'chore',
        title: 'Chore',
        description: ['Target settings button relocation and initial step fix,', 'Auth loading message fix,'],
      },
    ],
  },
  {
    version: '0.3.5',
    release_date: '2025-03-24T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Secrets table redesign',
        description: ['Added a copy popup feature for easier interaction,', 'Redesigned secrets table for improved usability.'],
      },
      {
        type: 'enhancement',
        title: 'Sensitive files, and DNS tables UI update',
        description: ['Fixed some alignment issues.'],
      },
      {
        type: 'enhancement',
        title: 'True/False fields reorganization',
        description: ['True/false strings are changed with ✓ and - in tables.'],
      },
      {
        type: 'enhancement',
        title: 'Expanded accordion styling ',
        description: ['Added left border for opened accordion sections for better distinguishability.'],
      },
      {
        type: 'bugfix',
        title: 'Select/Edit scan validation',
        description: ["Ensured that at least one field must be selected in the 'Select Scan Type' section."],
      },
      {
        type: 'chore',
        title: 'Target action button relocation',
        description: ['Action button column is relocated in targets table.'],
      },
    ],
  },
  {
    version: '0.4.0',
    release_date: '2025-04-01T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Report APIs new structure',
        description: ['Improved table state handling with clear visual separation for:  No data available - Scan in progress - No scan selected - No incidents found'],
      },
      {
        type: 'enhancement',
        title: 'Auth services renewed',
        description: ['Auth structure is renewed for faster access,'],
      },
    ],
  },
  {
    version: '0.4.1',
    release_date: '2025-04-02T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: ['Email validation for login/register pages,', 'Toggle visiblity of passwords,', 'Technologies table view is updated with collapsible rows,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfixes',
        description: ['Target edit form state fixes'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Excel download fixes for latest API structure'],
      },
    ],
  },
  {
    version: '0.4.2',
    release_date: '2025-04-04T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: ['Header page is updated with latest structure,', 'Sidemenu is developed for mobile devices,', 'Added loader spinner and tooltip for targets in progress.'],
      },
      {
        type: 'bugfix',
        title: 'Bugfixes',
        description: ['Acordion bugs fixes in Subdomains and IP Addresses tabs,', 'Target edit navigation fix,'],
      },
    ],
  },
  {
    version: '0.4.3',
    release_date: '2025-04-05T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Targets table',
        description: ['Spinner feedback until pause/resume button is resolved,', 'Domain can be selected from targets table as the domain dropdown.'],
      },
      {
        type: 'chore',
        title: 'Handle token expiration',
        description: ['Users are now notified and redirected to the login page when their session token expires or is invalid.'],
      },
    ],
  },
  {
    version: '0.4.4',
    release_date: '2025-04-06T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Subdomains table',
        description: ['Static pagination is feature is added for IP Addresses tab,'],
      },
      {
        type: 'enhancement',
        title: 'Auto-expanded single row',
        description: ['When a table has only one row content, it is pre-expanded for easier access.'],
      },
      {
        type: 'enhancement',
        title: 'Other UI improvements',
        description: [
          'Centralized typography configurations for table rows and collapsibles,',
          'Negative results are located at the top in certificates and dns tables,',
          'Chips has better interaction and takes less space for a better view,',
          'Icons for chips in headers page is added'
        ],
      },
    ],
  },
  {
    version: '0.5.0',
    release_date: '2025-04-12T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Tech stack migration',
        description: ['Upgraded core dependencies: Vite 6, MUI 7, and React 19,', 'Implemented comprehensive tree shaking for optimal bundle size.'],
      },
      {
        type: 'enhancement',
        title: 'Package optimization & internalization',
        description: [
          'Upgraded 10+ core packages to latest stable versions,',
          'Removed deprecated dependencies and replaced with lightweight alternatives or created custom solutions,',
          'Achieved 30% reduction in bundle size through strategic replacements.',
        ],
      },
      {
        type: 'enhancement',
        title: 'Performance optimizations',
        description: 'Reduced initial load requests by 72% (284 → 50) and improved Lighthouse score from 52 to 76.',
      },
      {
        type: 'enhancement',
        title: 'Lazy loading strategy',
        description: 'Added dynamic imports for heavy resources (charts, XLSX exporter) and optimized i18n with hooks.',
      },
      {
        type: 'enhancement',
        title: 'Responsive UI improvements',
        description: 'Fixed layout issues in domain add modal and sidebar components across all viewports.',
      },
      {
        type: 'enhancement',
        title: 'Compression enabled',
        description: 'Enabled GZIP compression for all static assets to improve transfer efficiency.',
      },
      {
        type: 'bugfix',
        title: 'Domain selection state fix',
        description: 'Resolved state management issue with preset URLs in domain selector component.',
      },
      {
        hidden: true,
        type: 'chore',
        title: 'Packages upgraded ',
        description: ['Upgraded all packages with latest versions for performance and security:', 'vite, react, react-dom, react-redux, reduxjs/toolkit, react-apexcharts, '],
      },
      {
        hidden: true,
        type: 'chore',
        title: 'Removed packages',
        description: [
          'react-intersection-observer, apexcharts, react-quill, react-slick, slick-carousel, redux, simplebar, stylis-plugin-rtl, typescript, react-big-calendar, emoji-picker-react, @types/chance, framer-motion, moment, @azure/msal-browser, @azure/msal-react,  axios-mock-adapter, @types/react-slick, xlsx, formik, lodash, yup,dayjs, react-router, @emotion/styled, @casl/react, @casl/ability',
        ],
      },
    ],
  },
  {
    version: '0.5.1',
    release_date: '2025-04-16T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: [
          'New popup to select domain instead of dropdown when sidemenu is collapsed,',
          'Hover sidemenu is disabled for more stable view,',
          'Added tooltips on menu items when sidemenu is collapsed,',
          'Tooltip content enhancements for targets table.',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Type-strict version log,',
          'Loading states are updated in redux for fetchCharts,',
          'Secrets table collapsible width fix,',
          'Only subrows with CVE values are listed on the vulnerabilities page,',
          'Technologies table column witdhs are adjusted for better visibility,',
          'Scrollbar theme colors are adjusted with softer tones,',
          'Fixed pre-expanded state of search inputs,',
          'Version badge is also visible on collapsed sidemenu,',
          'Version history is only visible for logged-in users,',
          "Subdomains and IP Addresses table's <NoIncidents> text and image are customized,",
          'Icon of selected domain in targets table is changed from badge to radio,',
        ],
      },
      {
        type: 'chore',
        title: 'Fixed font size',
        description: ['For subrows in sensetive files page,', 'For headers page subrows,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Error message after failed request of domain add is now dynamic,',
          'Download xlsx button functionality is fixed in technologies page,',
          'Add domain popup is fixed for overflow screen when over-zoomed,',
          'Logout button in bottom actions is fixed to login page afterwards.',
        ],
      },
    ],
  },
  {
    version: '0.5.2',
    release_date: '2025-04-18T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Dns page row data type handling for null values,', 'Clear non-domain related data on domain change,', 'Scroll location reset after domain change,'],
      },
      {
        type: 'chore',
        title: 'Better accessibility',
        description: 'Updated aria-labels for better accessibility.',
      },
    ],
  },
  {
    version: '0.5.3',
    release_date: '2025-04-23T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Null rows are cleared from listing in dns page,', 'Copy fix in secrets page,', 'Chips with null pass value are removed from the chip list in headers page,'],
      },
      {
        type: 'chore',
        title: 'Overview page',
        description: 'Renewed overview page for faster load and easier navigation'
      },
    ],
  },
  {
    version: '0.6.0',
    release_date: '2025-04-24T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: '2FA',
        description: ['2FA is implemented to get an extra layer of security'],
      },
      {
        type: 'feature',
        title: 'Auth',
        description: ['Forgot, reset, and change password pages are added'],
      },
    ],
  },
  {
    version: '0.6.1',
    release_date: '2025-04-27T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Features',
        description: ['Users can change their account emails', 'New contact information section in account settings page'],
      },
      {
        type: 'enhancement',
        title: 'Enhancements',
        // 'Screenshots of domains can be viewed using icon button in targets page > collapsible row start',
        description: ['Status data of targets is included in a single fetch'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Additional setup request is prevented on tab switches',
          'Better info tooltip messages for updating account email',
          'Notifications and account settings fix',
          'Stability on auth token',
          'Responsive fix for account settings page',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Sidemenu expand after redirection from overall page is fixed', 'Fix DNS scan results layout overflow and reset open accordions on domain change', 'Reset password token issue is fixed'],
      },
    ],
  },
  {
    version: '0.6.2',
    release_date: '2025-05-11T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Features',
        description: ['Shortcut to open search panel: Ctrl + Shift + F,', 'Host tab in ports page now supports pagination,'],
      },
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: ['Dns table info tooltip > "This scan is done on the main domain",', 'The side menu now supports multiple expandable sections that retain their state until the tab is closed,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Subdomain takeover row keys are updated,'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Standart page size is changed from 5 to 25 in pages that supports static pagination,', 'Rerender in version log panel is fixed,', 'Scroll position now resets after domain change,'],
      },
      {
        hidden: true,
        type: 'chore',
        title: 'Chore',
        description: ['Llm api (llm_report) requests are removed,', 'All logs are silenced in dash,'],
      },
    ],
  },
  {
    version: '0.6.3',
    release_date: '2025-05-19T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'New Page',
        description: 'New overview page is added, making it easier to view and manage all critical findings.',
      },
      {
        type: 'feature',
        title: 'New Page',
        description: 'Web Application Firewall (WAF) page is added.',
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Strict domain validation'],
      },
      {
        type: 'chore',
        title: 'Chore',
        hidden: true,
        description: ['Developed api-config.ts and retructured App.tsx for better maintainability'],
      },
    ],
  },
  {
    version: '0.6.4',
    release_date: '2025-05-21T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Responsive enhancements for card titles in integrations page'],
      },
      {
        type: 'enhancement',
        title: 'Findings page enhancement',
        description: [
          'Severity names are created dynamically and has improved styling,',
          'Responsive enhancements,',
          'Cards are compacted for better visibility',
          'Right panel is optimized for optic focusability and readability',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Findings total counts are fixed', 'Table data load bug on findings page'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['No-findings-selected image is changed to gif'],
      },
    ],
  },
  {
    version: '0.6.5',
    release_date: '2025-05-22T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Feature',
        description: ['Findings page now has a filtering feature'],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Findings page theme optimizations'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Waf table only shows items with firewall ≠ None'],
      },
    ],
  },
  {
    version: '0.7.0',
    release_date: '2025-05-27T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Feature',
        description: ['Integration page functionality is implemented,'],
      },
    ],
  },
  {
    version: '0.7.1',
    release_date: '2025-06-01T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Integration page functionality is developed,', 'Loading skeletons for targets page,', 'Loading skeletons for findings page,'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Dark mode background color is fixed on findings page,', 'zIndex for modal close button is fixed,', 'Scrollbar wrappers in findings page are fixed,'],
      },
    ],
  },
  {
    version: '0.7.2',
    release_date: '2025-06-05T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Integrations > `How to get a Webhook URL?` popups are added,', 'Notification preferences now show a redirect tooltip and refetch data when the dropdown is opened,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Scan pause and resume actions are fixed,'],
      },
    ],
  },
  {
    version: '0.7.3',
    release_date: '2025-06-07T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: [
          'Better view for selected notification preferences,',
          'Notification preferences table is moved to seperated tab for better usability,',
          'Notification preferences enhanced info tooltips, modals, and badge dots to distinguish integrated channels,',
          'Screenshot displayer is added to all summary components,',
          'Severity chips in findings page are now ordered by severity priority,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Icon width is fixed in technologies page,'],
      },
    ],
  },
  {
    version: '0.7.4',
    release_date: '2025-06-12T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Locked view for side menu items that are not included in the current scan,', 'Minor improvements for notification fetch,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Resume scan button is fixed,'],
      },
    ],
  },
  {
    version: '0.7.5',
    release_date: '2025-06-14T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Side menu scan-restricted locked-view and redirection to first unrestricted child is fixed,'],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: [`All table row's hover and expand behaviours are fixed,`],
      },
    ],
  },
  {
    version: '0.7.6',
    release_date: '2025-06-17T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Redirection after selected domain is changed,'],
      },
    ],
  },
  {
    version: '0.7.7',
    release_date: '2025-06-18T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Analysis running progress tooltip in targets page is detailed with progress percentage and remaining scan count,'],
      },
    ],
  },
  {
    version: '0.7.8',
    release_date: '2025-06-28T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['CI/CD pipeline is implemented,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['One time scan is fixed,'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Id usage is changed for reporting APIs,'],
      },
    ],
  },
  {
    version: '0.7.9',
    release_date: '2025-06-29T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Vulnerabilities table is redesigned,'],
      },
    ],
  },
  {
    version: '0.8.0',
    release_date: '2025-06-30T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Implemented refresh-access token cycle,'],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Updated some table cell views for one-time scan limitations in targets table,'],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ['Manual scan status on targets table is fixed,'],
      },
    ],
  },
  {
    version: '0.8.1',
    release_date: '2025-07-06T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ['Former card on targets page is replaced for better UX,', "Add-domain table action button is added on targets table,"],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ['Flag icons are updated,'],
      },
    ],
  },
  {
    version: '0.9.0',
    release_date: '2025-07-10T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Feature',
        description: ["Plugin integration has been implemented (on beta only),"],
      },
      {
        type: 'feature',
        title: 'Feature',
        description: ["History selection of scans has been initialized (on beta only),"],
      },
      {
        type: 'feature',
        title: 'Feature',
        description: ["Static Code Analysis page has been implemented,"],
      },
    ],
  },
  {
    version: '0.9.1',
    release_date: '2025-07-15T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Overview page is updated with minor design changes, and displays CWE and CVE,", "Targets page is updated with minor design changes"],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ["One time scan target settings are disabled,"],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ["Click domain to switch is fixed,", "Scan now button is fixed for one time scans,"],
      },
    ],
  },
  {
    version: '0.9.2',
    release_date: '2025-07-17T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Add new domain dialog now checks url scannability at step 1,", "Static Code Analysis page is updated with latest design changes,", "Scan history progress on subdomains page (on development),"],
      },
    ],
  },
  {
    version: '0.9.3',
    release_date: '2025-07-19T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Plugin integration has been completed,"],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Scan progress updates in targets page without requiring page reload,", "External link confirmation dialog is added to all external links,"],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ["Minor code refactoring and design fixes",],
      },
    ],
  },
  {
    version: '0.9.4',
    release_date: '2025-07-23T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Feature',
        description: ["Feedback feature is implemented,"],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["History date picker is rebuilded with user-friendly new design,", "History selection feature is completed for all report tables,",],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Vulnerabilities table now has a CWE column and is mobile-friendly,"],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ["Secuseek API key generation dialog is fixed for blank page,", "Integration tab is fixed for dynamic text and badge dots,", "CVE overflow on vulnerabilities table is fixed,"],
      },
      {
        type: 'chore',
        title: 'Design tweaks and minor fixes',
        description: ['Hydration errors', "Overview search input,", 'Fixes on some undesired external redirects,', 'Updated terminology and wording for improved clarity,'],
      },
    ],
  },
  {
    version: '0.9.5',
    release_date: '2025-07-24T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Overview page now has historical data picker,",],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Custom hook to check page availability after selected domain changes and handles redirections,", "Target's name can be set and updated,", "Summary cards now display status for historical data,"],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: ["Plans page is updated with new content,"],
      },
    ],
  },
  {
    version: '0.9.6',
    release_date: '2025-07-25T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: ["Settings page tabs are made horizontal scrollable,",],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: ["Reset history and fix refetch bug on domain change,", "Domain selection input reset after scan interval refetch is fixed,",],
      },
      {
        type: 'chore',
        title: 'Minor',
        description: ["Updated wording in multiply components for improved clarity,", "Feedback button is moved from top header to corner as a floating button,", "Add new button is fixed for Safari browser compatible,"],
      },
    ],
  },
  {
    version: '0.9.7',
    release_date: '2025-07-26T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Overview Page Overflow',
        description: [
          'Overview page overflow is fixed,'
        ],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: [
          'Mobile sidemenu is closed after add new domain is finished,',
          'Version History dialog now can be closed with escape key,',
          'Select Scan History dialog now can be closed with escape key,',
        ],
      },
      {
        type: 'enhancement',
        title: 'Mobile usability and responsive fixes',
        description: [
          'Direct url visit to report pages doesnt fetch data on mobile,',
          'Select Scan History dialog responsive fixes for xs devices,',
          'Requests page responsive fixes,',
        ],
      },
    ],
  },
  {
    version: '0.9.8',
    release_date: '2025-07-27T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Mobile Responsive Fixes',
        description: [
          'Fixed mobile breadcrumb placement on all pages,',
          'Hidden target settings button label on mobile sidebar is fixed,',
          'Hidden add domain button label on mobile sidebar is fixed,',
          'Stepper on add new domain dialog is fixed for mobile,',
          'Domain change before scan start warning is fixed,'
        ],
      },
      {
        type: 'enhancement',
        title: 'UI Improvements',
        description: [
          'Breadcrumb is hidden on all public pages,',
          'Overview page responsive fixes,'
        ],
      },
      {
        type: 'enhancement',
        title: 'Authentication Feedback',
        description: [
          '"You are not logged in. Please login to continue" warning added,'
        ],
      },
    ],
  },
  {
    version: '0.9.9',
    release_date: '2025-07-29T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Feature',
        description: ["Overview page now has a 'Filter with this finding' button in actions menu that helps issue cards to be filtered according to selected finding's properties,"],
      },
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: [
          'MUI version is updated to 7.20 (latest),',
          "Add domain popup's error messages are detailed and more pre-checks are added,",
          'Secrets page collapsible view is improved,',
        ],
      },
      {
        type: 'enhancement',
        title: 'Responsive Improvements',
        description: [
          'Pages : Overview, Technologies, Malicious, Vulnerabilities, Secrets,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Scan history selection lost on mobile is fixed,',
          'Spinner is fixed for full screen,'
        ],
      },
    ],
  },
  {
    version: '1.0.0-beta.0',
    release_date: '2025-07-30T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: [
          'Pre-release version related functions are implemented | Version chip is updated with pre-release indicator,',
          'Overview page -filter with this finding dialog- is improved for mobile,',
          'External link confirmation dialog is added with copy link button,'
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Add domain popup is fixed for handling initial value on opening,',
          'Target edit dialog is fixed for handling initial value on opening,',
          'Requests page responsive fixes for history warning,'
        ],
      },
    ],
  },
  {
    version: '1.0.0-beta.1',
    release_date: '2025-08-01T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Findings page improvements',
        description: [
          'Relocation of small components for better UX,',
          'Group-by and order-by added,',
          'Filter toggle button is added',
          'Version history dialog is improved for mobile,',
          'Version chip is improved,',
        ],
      },
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: 'Loading state added for scan history picker,',
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Shrinked styling of labels,',
          'Minor fixes and improvements for codebase,',
          'Fine-tuning for states of filters and buttons,',
          'Status uses friendly namings,'
        ],
      },
    ],
  },
  {
    version: '1.0.0-beta.2',
    release_date: '2025-08-15T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Profile Settings Improvements',
        description: [
          'Integrations tab is re-built for easier user experience and named App-Integrations,',
          'Notification preferences are filtered by isVisible,',
          'User Profile page responsive enhancements,',
        ],
      },
      {
        type: 'enhancement',
        title: 'Version Chip',
        description: 'Version chip is updated with pre-release version,',
      },
      {
        type: 'bugfix',
        title: 'Minor Fixes',
        description: 'Browser compatibility and minor fixes,',
      },
      {
        type: 'chore',
        title: 'Chore',
        description: 'Technologies table rows are made clickable,',
      },
    ],
  },
  {
    version: '1.0.1-beta.0',
    release_date: '2025-08-22T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'User management for administrators',
        description: [
          'User-list and user-details pages are added,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Cookies tab is removed from headers table,',
          'Cloud and Infrastructure menu item is removed,',
        ],
      },
    ],
  },
  {
    version: '1.0.1-beta.1',
    release_date: '2025-08-24T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancement',
        description: [
          'Table action buttons across all pages now have improved alignment and consistent spacing with responsive design.'
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Version history dialog is optimized for rerender issues,',
          'User management page, user list role filter is corrected,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'User management page, user details is updated with latest response format,',

        ],
      },
    ],
  },
  {
    version: '1.0.1-beta.2',
    release_date: '2025-08-26T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Publications Page',
        description: 'Publications page is added for json parsing performance testing.',
      },
      {
        type: 'bugfix',
        title: 'Delete Target Behaviour Fix',
        description: 'Fixed delete target behaviour to ensure proper functionality.',
      },
      {
        type: 'enhancement',
        title: 'Toast Messages Enhancement',
        description: 'Enhanced toast messages for better user experience.',
      },
      {
        type: 'bugfix',
        title: 'Notification Request After Logout Fix',
        description: 'Fixed notification requests that were occurring after logout.',
      },
      {
        type: 'enhancement',
        title: 'Target Add/Update Handling Enhancement',
        description: 'Enhanced target add and update handling for improved reliability.',
      },
      {
        type: 'enhancement',
        title: 'Malicious Table Row Content Simplification',
        description: 'Simplified malicious table row content for better readability.',
        issue_number: 223,
      },
      {
        type: 'bugfix',
        title: 'User Inactive Status Login Handling',
        description: 'Properly handled user inactive status during login process.',
      },
      {
        type: 'bugfix',
        title: 'Collapsible Row Buttons Fix',
        description: 'Fixed collapsible row buttons in 12 tables for style consistency.',
      },
      {
        type: 'enhancement',
        title: 'User Details Page Redesign',
        description: 'User details page has been completely rebuilt with latest design and functionality.',
      },
    ],
  },
  {
    version: '1.0.2-beta.0',
    release_date: '2025-09-03T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Performance Optimizations',
        description: [
          'After delete target → update statically (no fetch)',
          'After "Scan Now" → update row statically (no fetch)',
          'Admin-user edit → update list statically',
        ],
      },
      {
        type: 'enhancement',
        title: 'User Experience Improvements',
        description: [
          '"Scan Now" → add spinner + toast messages',
          'Integrations page → alert icons in popup',
          'Remove channel type in edit notification preference popup',
        ],
      },
      {
        type: 'enhancement',
        title: 'UI/UX Enhancements',
        description: [
          'Tables → keep border below last row too',
          'Third-party → standardize row heights',
          'Third-party → remove card outer container, add bottom padding',
        ],
      },
      {
        type: 'bugfix',
        title: 'User Management Fixes',
        description: [
          'User-management page → role type handling is fixed',
          'User-detail → fix editable fields',
          'Scan now button → fix bug updating schedule scan afterwards',
        ],
      },
    ],
  },
  {
    version: '1.0.2-beta.1',
    release_date: '2025-09-05T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Vulnerabilities Table Enhancements',
        description: [
          'Actively exploited indicators are added to → Summary card + Table row + CVE table sub-row,',
        ],
      },
      {
        type: 'enhancement',
        title: 'UI/UX Enhancements',
        description: [
          'User management page responsive enhancement, and table orderings',
        ],
      },
    ],
  },
  {
    version: '1.0.3-beta.0',
    release_date: '2025-09-17T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Technologies Table Enhancements',
        description: [
          'Date, and Status columns are added to → Technologies table row,',
        ],
      },
      {
        type: 'enhancement',
        title: 'UI/UX Enhancements',
        description: [
          'Actively exploited vulnerability indicators are updated and detection logic improved → Vulnerabilities page,',
        ],
      },
    ],
  },
  {
    version: '1.0.3-beta.1',
    release_date: '2025-09-19T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'UI/UX Enhancements',
        description: [
          'Version history dialog is improved for smaller dom size,',
          'User management enhancements → pagination states + clickable rows,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Subdomain takeover table container name is fixed,',
        ],
      },
    ],
  },
  {
    version: '1.0.4-beta.0',
    release_date: '2025-09-21T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'UI/UX Enhancements',
        description: [
          'Pagination and rows per page selector is added → Overview page,',
          'Sub-row toggling for full description is improved → Technologies page,',
          'Scan history picker is improved,',
          'Technologies page tech rows are sorted by risk status,',
          'Filters are loaded as hidden on mobile → Overview page,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Ports summary is fixed,',
          'DNS page sub-table cell overflow is fixed,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Header page tab format is updated,',
          'Targets page table cell padding is updated on mobile,',
        ],
      },
    ],
  },
  {
    version: '1.0.5-beta.0',
    release_date: '2025-10-02T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'DNS/Port detection feature',
        description: [
          'DNS change alerts implemented → DNS page,',
          '"New" badge is added → Ports page tabs,',
        ],
      },
      {
        type: 'feature',
        title: 'AuthGuard',
        description: [
          'AuthGuard is implemented to protect routes that are not accessible to unauthenticated users before api-calls start,',
        ],
      },
      {
        type: 'enhancement',
        title: 'UI/UX Enhancements',
        description: [
          'Initial rows per page is stored in local-storage and set to 50 → Overview page,',
          'Version history sorting is enhanced by both date and version name,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Jenkinsfile is updated for deployment,',
        ],
        hidden: true,
      },
    ],
  },
  {
    version: '1.0.5-beta.1',
    release_date: '2025-10-07T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Overview UI/UX Enhancements',
        description: [
          'Sorting buttons are added with up and down icons,',
          'New color added to theme palette and used for bg of both panels,',
          'Filter selectors, search input, and action buttons are updated with new design,',
          'Finding severity chips are updated with new colors,',
          'Pagination is updated with new design,',
          'Finding cards have left border when active,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Vulnerabilities table exploited indicators → total count is fixed,',
        ],
      },
      {
        type: 'chore',
        title: 'Other changes',
        description: [
          'Font is replaced with local woff2 instead of Google Fonts to comply with GDPR requirements in Germany,',
          'Header background is made transparent changing to white on hover,',
        ],
      },
    ],
  },
  {
    version: '1.1.0-beta.0',
    release_date: '2025-10-11T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Instant status updates using websocket',
        description: [
          'Instant update for scan statuses for progress updates using websocket,',
        ],
      },
    ],
  },
  {
    version: '1.1.0-beta.1',
    release_date: '2025-10-15T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Socket fixes',
        description: [
          'Fix realtime connection of target status updates,',
        ],
      },
    ],
  },
  {
    version: '1.1.0-beta.2',
    release_date: '2025-10-21T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: [
          'Targets page → unselected scan icon is updated and tooltip is added,',
          'Technologies page → Software Age column is added,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Socket fixes',
        description: [
          'Fix realtime reconnection issues,',
          'Fix update related rerender issues,',
          'Fix progress percentage fallback issues,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Minor fixes and code cleanup,',
        ],
      },
    ],
  },
  {
    version: '1.1.1-beta.0',
    release_date: '2025-10-22T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Chatbot feature',
        description: [
          'Chatbot feature is added to overview page to answer questions about the vulnerabilities,',
        ],
      },
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: [
          'Enhanced 2FA authentication flow,',
          'Centralized token management,',
          'Overview page → finding cards background color is updated to match severity color,',
          'Targets page → selected/unselected scan icon and tooltip are updated for clarity,',
          'Administrator page is relocated for easier access',
        ],
      },
    ],
  },
  {
    version: '1.1.1-beta.1',
    release_date: '2025-11-04T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: [
          'Targets page → collapsible row enhancements for included/excluded scan visual clarity,',
          'Chatbot feature → is now only visible for superusers,',
        ],
      },
    ],
  },
  {
    version: '1.2.0-beta.0',
    release_date: '2025-11-06T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'AI-powered action plans',
        description: [
          'Action Plan feature is initialized to generate action plans for these pages:',
          'DNS, Vulnerabilities, Headers, Ports, Certificates',
        ],
      },
      {
        type: 'enhancement',
        title: 'Enhancements',
        description: [
          'Notification preferences are simplified for better UX in add new domain dialog,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Refactored summary and action plan cards into modular, reusable format across all pages, reducing bundle size,',
        ],
      },
    ],
  },
  {
    version: '1.2.0-beta.1',
    release_date: '2025-11-09T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Action plan enhancements',
        description: [
          'Action plan is redesigned for better UX,',
        ],
      },
      {
        type: 'enhancement',
        title: 'Toast message enhancements',
        description: [
          'Toast messages are enhanced with subtitle and frozen progress bar on hover,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'BubbleChat reinitialization issue is fixed',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Sensitive resources table row alignment minor improvement',
        ],
      },
    ],
  },
  {
    version: '1.2.0-beta.2',
    release_date: '2025-11-19T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Action plan enhancements',
        description: [
          'Action plan design and animations are improved,',
        ],
      },
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Generate API Key button minor bugfix,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        description: [
          'Action plan countdown timer is updated to 15 seconds',
          'SecuSeek letter casing is updated on some pages',
        ],
      },
    ],
  },
  {
    version: '1.2.0-beta.3',
    release_date: '2025-11-21T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Action plan',
        description: [
          'Action plan is modularized and design is improved,',
        ],
      },
    ],
  },
  {
    version: '1.2.0-beta.4',
    release_date: '2025-11-24T00:00:00Z',
    changes: [
      {
        type: 'enhancement',
        title: 'Notification preferences',
        description: [
          'Notification preference adding on new domain dialog for easier UX,',
        ],
      },
    ],
  },
  {
    version: '1.2.0-beta.5',
    release_date: '2025-12-07T00:00:00Z',
    changes: [
      {
        type: 'chore',
        title: 'Action plan',
        description: [
          'Action plan error handling for retry,',
        ],
      },
    ],
  },
  {
    version: '1.2.1-beta.0',
    release_date: '2025-12-30T00:00:00Z',
    changes: [
      {
        type: 'bugfix',
        title: 'Bugfix',
        description: [
          'Add new domain dialog url scannability check is fixed,',
          'Toast message remaining time bug is fixed,',
        ],
      },
    ],
  },
  {
    version: '1.2.2-beta.0',
    release_date: '2026-06-30T00:00:00Z',
    changes: [
      {
        type: 'feature',
        title: 'Cyber Risk Score (CRS) Assessments',
        description: [
          'New CRS Assessments page is added under Results to prepare a cyber risk score profile,',
          'Company profile and questionnaire are collected through a step-by-step wizard with less scrolling,',
          'AI-powered cyber risk report generation is supported,',
          'Targets table shows a per-domain CRS column: calculate when the scan is completed, or view the colored score once available,',
        ],
      },
      {
        type: 'chore',
        title: 'Chore',
        hidden: true,
        description: [
          'Added dev:hub script to run the app locally against the hub environment,',
        ],
      },
    ],
  },
];
